import { useEffect, useMemo, useState } from 'react';

import {
  App,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminCityRow, AdminRegion } from 'models/Lead';
import { CreateStoreInput, Store, UpdateStoreInput } from 'models/Store';

import { LeadService } from 'services/LeadService';
import { StoreService } from 'services/StoreService';

import './StoreFormModal.scss';

const COUNTRY = 'CL';
/** Región Metropolitana en el catálogo Portal GO. */
const CAPITAL_REGION_ID = '13';
const CAPITAL_REGION_LABEL = 'Metropolitana (Capital)';

const normalize = (value: string | null | undefined) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

interface FormValues {
  name: string;
  code: string;
  chain: string;
  regionId?: string;
  communeId?: number;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  operationalNotes?: string;
  isVip: boolean;
  isActive: boolean;
}

interface StoreFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  store?: Store | null;
  onCancel: () => void;
  onSaved: () => void;
}

function StoreFormModal({
  isOpen,
  mode,
  store,
  onCancel,
  onSaved,
}: StoreFormModalProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const i18nBase = 'scenes.stores.components.store_form_modal';

  const [regions, setRegions] = useState<AdminRegion[]>([]);
  const [communes, setCommunes] = useState<AdminCityRow[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);

  const regionId = Form.useWatch('regionId', form);
  const address = Form.useWatch('address', form);
  const latitude = Form.useWatch('latitude', form);
  const longitude = Form.useWatch('longitude', form);

  const isEdit = mode === 'edit';
  const isCapital = regionId === CAPITAL_REGION_ID;

  useEffect(() => {
    if (!isOpen || regions.length > 0) return;
    (async () => {
      setCatalogsLoading(true);
      const [regionsResponse, communesResponse] = await Promise.all([
        LeadService.getRegions(COUNTRY),
        LeadService.getCities(COUNTRY, CAPITAL_REGION_ID),
      ]);
      setCatalogsLoading(false);
      const failed = [regionsResponse, communesResponse].find(
        (response) => !response.success
      );
      if (failed) {
        const error = failed.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.catalogs`),
          3.5
        );
        return;
      }
      setRegions((regionsResponse.data as AdminRegion[]) || []);
      setCommunes((communesResponse.data as AdminCityRow[]) || []);
    })();
  }, [isOpen, regions.length, message, t, i18nBase]);

  const regionOptions = useMemo(
    () =>
      [...regions]
        .sort((a, b) => {
          if (a.regionId === CAPITAL_REGION_ID) return -1;
          if (b.regionId === CAPITAL_REGION_ID) return 1;
          return a.regionName.localeCompare(b.regionName, 'es');
        })
        .map((region) => ({
          value: region.regionId,
          label:
            region.regionId === CAPITAL_REGION_ID
              ? t(`${i18nBase}.region_options.capital`)
              : region.regionName,
        })),
    [regions, t, i18nBase]
  );

  const communeOptions = useMemo(
    () =>
      [...communes]
        .sort((a, b) => a.cityName.localeCompare(b.cityName, 'es'))
        .map((city) => ({ value: city.id, label: city.cityName })),
    [communes]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && store) {
      form.setFieldsValue({
        name: store.name,
        code: store.code,
        chain: store.chain,
        regionId:
          store.regionKind === 'CAPITAL' ? CAPITAL_REGION_ID : undefined,
        communeId: store.communeId ?? undefined,
        address: store.address,
        latitude: store.latitude,
        longitude: store.longitude,
        operationalNotes: store.operationalNotes || undefined,
        isVip: store.isVip,
        isActive: store.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        regionId: CAPITAL_REGION_ID,
        isVip: false,
        isActive: true,
      });
    }
  }, [isOpen, isEdit, store, form]);

  // Salas antiguas solo tienen nombres: se preseleccionan por coincidencia.
  useEffect(() => {
    if (!isOpen || !isEdit || !store) return;
    if (!form.getFieldValue('regionId') && regions.length > 0) {
      const label = normalize(store.regionLabel);
      const match =
        store.regionKind === 'CAPITAL'
          ? CAPITAL_REGION_ID
          : regions.find((region) => {
              const name = normalize(region.regionName);
              return name === label || name.includes(label);
            })?.regionId;
      if (match) form.setFieldValue('regionId', match);
    }
    if (
      store.regionKind === 'CAPITAL' &&
      !form.getFieldValue('communeId') &&
      communes.length > 0
    ) {
      const match =
        communes.find((city) => city.id === store.communeId) ||
        communes.find(
          (city) => normalize(city.cityName) === normalize(store.commune)
        );
      if (match) form.setFieldValue('communeId', match.id);
    }
  }, [isOpen, isEdit, store, regions, communes, form]);

  const mapSearchUrl = useMemo(() => {
    if (
      typeof latitude === 'number' &&
      typeof longitude === 'number' &&
      !Number.isNaN(latitude) &&
      !Number.isNaN(longitude)
    ) {
      return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    }
    if (address?.trim()) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address.trim()
      )}`;
    }
    return 'https://www.google.com/maps';
  }, [address, latitude, longitude]);

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const capital = values.regionId === CAPITAL_REGION_ID;
    const region = regions.find((r) => r.regionId === values.regionId);
    const commune = capital
      ? communes.find((city) => city.id === values.communeId)
      : undefined;
    const location = {
      regionKind: capital ? ('CAPITAL' as const) : ('REGION' as const),
      regionLabel: capital
        ? CAPITAL_REGION_LABEL
        : region?.regionName || store?.regionLabel || '',
      commune: commune?.cityName || null,
      communeId: commune?.id ?? null,
    };

    setSubmitting(true);

    if (isEdit && store) {
      const body: UpdateStoreInput = {
        name: values.name,
        chain: values.chain,
        ...location,
        address: values.address,
        latitude: values.latitude ?? null,
        longitude: values.longitude ?? null,
        operationalNotes: values.operationalNotes?.trim() || null,
        isVip: !!values.isVip,
        isActive: !!values.isActive,
      };
      const response = await StoreService.update(store.id, body);
      setSubmitting(false);
      if (response.success) {
        message.success(t(`${i18nBase}.messages.update_success`), 3.5);
        onSaved();
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.update`),
          3.5
        );
      }
      return;
    }

    const body: CreateStoreInput = {
      name: values.name,
      code: values.code,
      chain: values.chain,
      ...location,
      address: values.address,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      operationalNotes: values.operationalNotes?.trim() || null,
      isVip: !!values.isVip,
      isActive: !!values.isActive,
    };
    const response = await StoreService.create(body);
    setSubmitting(false);
    if (response.success) {
      message.success(t(`${i18nBase}.messages.create_success`), 3.5);
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      const fallback =
        error.code === 'STORE_CODE_DUPLICATE'
          ? t(`${i18nBase}.errors.code_duplicate`)
          : t(`${i18nBase}.errors.create`);
      message.error((error.message as string) || fallback, 3.5);
    }
  };

  return (
    <Modal
      open={isOpen}
      title={
        <div className="store-form-modal__title">
          <div>
            {isEdit
              ? t(`${i18nBase}.title_edit`)
              : t(`${i18nBase}.title_create`)}
          </div>
          {isEdit && store && (
            <div className="store-form-modal__subtitle">{store.code}</div>
          )}
        </div>
      }
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={
        isEdit ? t(`${i18nBase}.buttons.save`) : t(`${i18nBase}.buttons.create`)
      }
      cancelText={t(`${i18nBase}.buttons.cancel`)}
      confirmLoading={submitting}
      destroyOnClose
      width={720}
      centered
      className="store-form-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark
        className="store-form-modal__form"
        initialValues={{
          regionId: CAPITAL_REGION_ID,
          isVip: false,
          isActive: true,
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label={t(`${i18nBase}.labels.name`)}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.name.required`),
                },
              ]}
            >
              <Input placeholder={t(`${i18nBase}.placeholders.name`)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="code"
              label={t(`${i18nBase}.labels.code`)}
              extra={
                isEdit
                  ? t(`${i18nBase}.helpers.code_immutable`)
                  : t(`${i18nBase}.helpers.code`)
              }
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.code.required`),
                },
              ]}
            >
              <Input
                disabled={isEdit}
                placeholder={t(`${i18nBase}.placeholders.code`)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="chain"
              label={t(`${i18nBase}.labels.chain`)}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.chain.required`),
                },
              ]}
            >
              <Input placeholder={t(`${i18nBase}.placeholders.chain`)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="regionId"
              label={t(`${i18nBase}.labels.region`)}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.region.required`),
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={catalogsLoading}
                placeholder={t(`${i18nBase}.placeholders.region`)}
                options={regionOptions}
                onChange={(value) => {
                  if (value !== CAPITAL_REGION_ID) {
                    form.setFieldValue('communeId', undefined);
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="communeId"
              label={t(`${i18nBase}.labels.commune`)}
              extra={t(`${i18nBase}.helpers.commune`)}
              required={isCapital}
              rules={[
                {
                  required: isCapital,
                  message: t(`${i18nBase}.validations.commune.required`),
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                disabled={!isCapital}
                loading={catalogsLoading}
                placeholder={t(`${i18nBase}.placeholders.commune`)}
                options={communeOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="address"
              label={t(`${i18nBase}.labels.address`)}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.address.required`),
                },
              ]}
            >
              <Input placeholder={t(`${i18nBase}.placeholders.address`)} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="latitude"
              label={t(`${i18nBase}.labels.latitude`)}
              extra={t(`${i18nBase}.helpers.optional`)}
            >
              <InputNumber
                style={{ width: '100%' }}
                step={0.0001}
                placeholder={t(`${i18nBase}.placeholders.latitude`)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="longitude"
              label={t(`${i18nBase}.labels.longitude`)}
            >
              <InputNumber
                style={{ width: '100%' }}
                step={0.0001}
                placeholder={t(`${i18nBase}.placeholders.longitude`)}
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="store-form-modal__map-link">
          <a href={mapSearchUrl} target="_blank" rel="noopener noreferrer">
            {t(`${i18nBase}.links.search_on_map`)}
          </a>
        </div>

        <Form.Item
          name="operationalNotes"
          label={t(`${i18nBase}.labels.operational_notes`)}
          extra={t(`${i18nBase}.helpers.operational_notes`)}
        >
          <Input.TextArea
            rows={3}
            placeholder={t(`${i18nBase}.placeholders.operational_notes`)}
          />
        </Form.Item>

        <Row gutter={12} className="store-form-modal__flags">
          <Col xs={24} md={12}>
            <Form.Item name="isVip" valuePropName="checked" noStyle>
              <Checkbox className="store-form-modal__flag">
                <span className="store-form-modal__flag-text">
                  <strong>{t(`${i18nBase}.labels.is_vip`)}</strong>
                  <small>{t(`${i18nBase}.helpers.is_vip`)}</small>
                </span>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Checkbox className="store-form-modal__flag">
                <span className="store-form-modal__flag-text">
                  <strong>{t(`${i18nBase}.labels.is_active`)}</strong>
                  <small>{t(`${i18nBase}.helpers.is_active`)}</small>
                </span>
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default StoreFormModal;
